/**
 * Orthogonal connector routing for diagram edges.
 *
 * Approach (industry-standard for node diagrams / VLSI maze routing):
 * 1. Inflate each device by BLOCK_BARRIER (1 dash) → keep-out zone
 * 2. Port stub leaves/enters outside the keep-out
 * 3. A* on a coarse grid: cost = distance + bendPenalty (prefer fewer turns)
 * 4. Never U-turn (180°); never step into keep-out (segment midpoint checked)
 * 5. Endpoints ARE obstacles (no tunneling through own block)
 *
 * Refs: maze/Hanan routing; orthogonal A* as in diagram tools (e.g. xNet router).
 */
import { Position } from '@xyflow/react';
import { DIAGRAM_GRID_SIZE, snapCoord } from '@ukrop-nm/shared';

export type ObstacleRect = {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
};

type Pt = { x: number; y: number };
type Dir = 0 | 1 | 2 | 3; // E N W S

export const EDGE_DASH_ARRAY = '6 6';
export const EDGE_DASH_PERIOD = 12;

/** Keep-out around every device (1 пунктир). */
export const BLOCK_BARRIER = EDGE_DASH_PERIOD;

/**
 * Leave/enter outside keep-out.
 * Must be > BLOCK_BARRIER so stub tip is strictly outside inflated box.
 */
export const PORT_STUB = BLOCK_BARRIER + DIAGRAM_GRID_SIZE / 2; // 20

/** Parallel corridors: 1 пунктир. */
export const LANE_GAP = EDGE_DASH_PERIOD;

const GRID = DIAGRAM_GRID_SIZE;
const BEND_PENALTY = GRID * 5; // strong preference for fewer corners
const MAX_ITERS = 14_000;
const SEARCH_PAD = GRID * 24;

const STEP: Array<{ dx: number; dy: number; dir: Dir }> = [
  { dx: GRID, dy: 0, dir: 0 },
  { dx: 0, dy: -GRID, dir: 1 },
  { dx: -GRID, dy: 0, dir: 2 },
  { dx: 0, dy: GRID, dir: 3 },
];

function almostEq(a: number, b: number, eps = 1.5): boolean {
  return Math.abs(a - b) <= eps;
}

function posToDir(pos: Position): Dir {
  switch (pos) {
    case Position.Right:
      return 0;
    case Position.Top:
      return 1;
    case Position.Left:
      return 2;
    case Position.Bottom:
      return 3;
    default:
      return 0;
  }
}

function dirVec(d: Dir): Pt {
  return STEP[d] ? { x: Math.sign(STEP[d]!.dx), y: Math.sign(STEP[d]!.dy) } : { x: 1, y: 0 };
}

function opposite(d: Dir): Dir {
  return ((d + 2) % 4) as Dir;
}

function inflate(r: ObstacleRect, pad: number): ObstacleRect {
  return {
    id: r.id,
    x: r.x - pad,
    y: r.y - pad,
    width: r.width + pad * 2,
    height: r.height + pad * 2,
  };
}

function toSvgPath(points: Pt[]): string {
  if (!points.length) return '';
  let d = `M ${points[0]!.x} ${points[0]!.y}`;
  for (let i = 1; i < points.length; i++) d += ` L ${points[i]!.x} ${points[i]!.y}`;
  return d;
}

function pathLength(points: Pt[]): number {
  let len = 0;
  for (let i = 0; i < points.length - 1; i++) {
    len += Math.hypot(points[i + 1]!.x - points[i]!.x, points[i + 1]!.y - points[i]!.y);
  }
  return len;
}

function manhattan(a: Pt, b: Pt): number {
  return Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
}

function forceOrtho(points: Pt[]): Pt[] {
  if (points.length <= 1) return points.map((p) => ({ ...p }));
  const out: Pt[] = [{ ...points[0]! }];
  for (let i = 1; i < points.length; i++) {
    const prev = out[out.length - 1]!;
    const cur = points[i]!;
    if (almostEq(prev.x, cur.x) || almostEq(prev.y, cur.y)) {
      out.push({
        x: almostEq(prev.x, cur.x) ? prev.x : cur.x,
        y: almostEq(prev.y, cur.y) ? prev.y : cur.y,
      });
      continue;
    }
    const prev2 = out.length >= 2 ? out[out.length - 2]! : null;
    if (prev2 && almostEq(prev2.y, prev.y)) out.push({ x: cur.x, y: prev.y });
    else if (prev2 && almostEq(prev2.x, prev.x)) out.push({ x: prev.x, y: cur.y });
    else out.push({ x: cur.x, y: prev.y });
    out.push({ ...cur });
  }
  return out;
}

function simplifyCollinear(points: Pt[]): Pt[] {
  if (points.length <= 2) return forceOrtho(points);
  const out: Pt[] = [points[0]!];
  for (let i = 1; i < points.length - 1; i++) {
    const prev = out[out.length - 1]!;
    const cur = points[i]!;
    const next = points[i + 1]!;
    const colinear =
      (almostEq(prev.x, cur.x) && almostEq(cur.x, next.x)) ||
      (almostEq(prev.y, cur.y) && almostEq(cur.y, next.y));
    if (!colinear && Math.hypot(cur.x - prev.x, cur.y - prev.y) > 1) out.push(cur);
  }
  out.push(points[points.length - 1]!);
  return forceOrtho(out);
}

function simplifyKeepStubs(points: Pt[]): Pt[] {
  const ortho = forceOrtho(points);
  if (ortho.length <= 4) return ortho;
  const first = ortho[0]!;
  const sExit = ortho[1]!;
  const tExit = ortho[ortho.length - 2]!;
  const last = ortho[ortho.length - 1]!;
  const mid = simplifyCollinear([sExit, ...ortho.slice(2, -2), tExit]);
  // mid starts with sExit and ends with tExit
  return forceOrtho([first, ...mid, last]);
}

function finalize(points: Pt[]): Pt[] {
  if (points.length < 4) return forceOrtho(points);
  const out = points.map((p) => ({ ...p }));
  const n = out.length;
  // Keep port coords exact; snap only stub ends & mids
  out[1] = {
    x: almostEq(out[0]!.y, out[1]!.y) ? snapCoord(out[1]!.x, GRID) : out[0]!.x,
    y: almostEq(out[0]!.x, out[1]!.x) ? snapCoord(out[1]!.y, GRID) : out[0]!.y,
  };
  out[n - 2] = {
    x: almostEq(out[n - 1]!.y, out[n - 2]!.y) ? snapCoord(out[n - 2]!.x, GRID) : out[n - 1]!.x,
    y: almostEq(out[n - 1]!.x, out[n - 2]!.x) ? snapCoord(out[n - 2]!.y, GRID) : out[n - 1]!.y,
  };
  for (let i = 2; i < n - 2; i++) {
    out[i] = { x: snapCoord(out[i]!.x, GRID), y: snapCoord(out[i]!.y, GRID) };
  }
  return simplifyKeepStubs(out);
}

function applyLaneOffset(points: Pt[], offset: number): Pt[] {
  if (Math.abs(offset) < 0.5 || points.length < 4) return points;
  const a = points[1]!;
  const b = points[points.length - 2]!;
  const horiz = Math.abs(b.x - a.x) >= Math.abs(b.y - a.y);
  return points.map((p, i) => {
    if (i <= 1 || i >= points.length - 2) return { ...p };
    return horiz ? { x: p.x, y: p.y + offset } : { x: p.x + offset, y: p.y };
  });
}

/** Point strictly inside keep-out (boundary is free for stubs). */
function insideKeepOut(x: number, y: number, box: ObstacleRect): boolean {
  return x > box.x + 0.5 && x < box.x + box.width - 0.5 && y > box.y + 0.5 && y < box.y + box.height - 0.5;
}

function pointBlocked(x: number, y: number, keepOut: ObstacleRect[]): boolean {
  for (const o of keepOut) {
    if (insideKeepOut(x, y, o)) return true;
  }
  return false;
}

/** Grid step is legal only if destination and segment mid stay outside keep-out. */
function stepClear(x0: number, y0: number, x1: number, y1: number, keepOut: ObstacleRect[]): boolean {
  if (pointBlocked(x1, y1, keepOut)) return false;
  const mx = (x0 + x1) / 2;
  const my = (y0 + y1) / 2;
  if (pointBlocked(mx, my, keepOut)) return false;
  return true;
}

function segmentHitsKeepOut(a: Pt, b: Pt, keepOut: ObstacleRect[]): boolean {
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  const steps = Math.max(8, Math.ceil(len / 6));
  for (let i = 1; i < steps; i++) {
    const t = i / steps;
    if (pointBlocked(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t, keepOut)) return true;
  }
  return false;
}

/** Corridor between stubs must not enter keep-out. */
function corridorHits(points: Pt[], keepOut: ObstacleRect[]): boolean {
  if (points.length < 4) return false;
  for (let i = 1; i <= points.length - 3; i++) {
    if (segmentHitsKeepOut(points[i]!, points[i + 1]!, keepOut)) return true;
  }
  return false;
}

function pointInRect(p: Pt, r: ObstacleRect, m = 0): boolean {
  return (
    p.x >= r.x - m &&
    p.x <= r.x + r.width + m &&
    p.y >= r.y - m &&
    p.y <= r.y + r.height + m
  );
}

export function pointAlongPath(points: Pt[], fraction: number): Pt {
  if (!points.length) return { x: 0, y: 0 };
  if (points.length === 1) return { ...points[0]! };
  const total = pathLength(points) || 1;
  let remain = total * Math.min(1, Math.max(0, fraction));
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i]!;
    const b = points[i + 1]!;
    const seg = Math.hypot(b.x - a.x, b.y - a.y);
    if (remain <= seg || i === points.length - 2) {
      const t = seg < 1e-6 ? 0 : remain / seg;
      return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
    }
    remain -= seg;
  }
  return { ...points[points.length - 1]! };
}

export type PathProjection = { t: number; point: Pt; lateral: number };

export function projectOntoPolyline(points: Pt[], x: number, y: number): PathProjection {
  if (!points.length) return { t: 0.5, point: { x, y }, lateral: 0 };
  if (points.length === 1) {
    const p = points[0]!;
    return { t: 0.5, point: { ...p }, lateral: Math.hypot(x - p.x, y - p.y) };
  }
  const total = pathLength(points) || 1;
  let bestDist = Infinity;
  let best: PathProjection = { t: 0.5, point: { ...points[0]! }, lateral: 0 };
  let acc = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i]!;
    const b = points[i + 1]!;
    const sx = b.x - a.x;
    const sy = b.y - a.y;
    const segLen = Math.hypot(sx, sy);
    if (segLen < 1e-6) continue;
    let u = ((x - a.x) * sx + (y - a.y) * sy) / (segLen * segLen);
    u = Math.max(0, Math.min(1, u));
    const px = a.x + sx * u;
    const py = a.y + sy * u;
    const dist = Math.hypot(x - px, y - py);
    const lateral = (sx * (y - a.y) - sy * (x - a.x)) / segLen;
    if (dist < bestDist) {
      bestDist = dist;
      best = { t: (acc + u * segLen) / total, point: { x: px, y: py }, lateral };
    }
    acc += segLen;
  }
  return best;
}

function labelPos(points: Pt[], keepOut: ObstacleRect[], nudgeY: number, labelT?: number): Pt {
  const fractions =
    labelT != null && Number.isFinite(labelT)
      ? [Math.min(0.88, Math.max(0.12, labelT))]
      : [0.5, 0.38, 0.62];
  for (const f of fractions) {
    const p = pointAlongPath(points, f);
    const q = { x: p.x, y: p.y + nudgeY };
    if (labelT != null || !keepOut.some((o) => pointInRect(q, o, 8))) return q;
  }
  const mid = pointAlongPath(points, 0.5);
  return { x: mid.x, y: mid.y + nudgeY };
}

// ─── A* ──────────────────────────────────────────────────────────────────────

type ANode = {
  x: number;
  y: number;
  dir: Dir;
  g: number;
  f: number;
  idx: number;
  parent: number;
};

class MinHeap {
  private h: ANode[] = [];
  get size() {
    return this.h.length;
  }
  push(n: ANode) {
    this.h.push(n);
    this.bubbleUp(this.h.length - 1);
  }
  pop(): ANode | undefined {
    if (!this.h.length) return undefined;
    const top = this.h[0]!;
    const last = this.h.pop()!;
    if (this.h.length) {
      this.h[0] = last;
      this.bubbleDown(0);
    }
    return top;
  }
  private bubbleUp(i: number) {
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (this.h[p]!.f <= this.h[i]!.f) break;
      [this.h[p], this.h[i]] = [this.h[i]!, this.h[p]!];
      i = p;
    }
  }
  private bubbleDown(i: number) {
    for (;;) {
      let s = i;
      const l = i * 2 + 1;
      const r = l + 1;
      if (l < this.h.length && this.h[l]!.f < this.h[s]!.f) s = l;
      if (r < this.h.length && this.h[r]!.f < this.h[s]!.f) s = r;
      if (s === i) break;
      [this.h[s], this.h[i]] = [this.h[i]!, this.h[s]!];
      i = s;
    }
  }
}

function stubTip(port: Pt, d: Dir): Pt {
  const v = dirVec(d);
  return { x: port.x + v.x * PORT_STUB, y: port.y + v.y * PORT_STUB };
}

/**
 * Route from sExit → tExit on the grid, staying outside keep-out.
 * startDir is the facing after leaving the source port (no immediate reverse).
 */
export function astarOrtho(
  sExit: Pt,
  tExit: Pt,
  startDir: Dir,
  keepOut: ObstacleRect[],
): Pt[] | null {
  const sx = snapCoord(sExit.x, GRID);
  const sy = snapCoord(sExit.y, GRID);
  const gx = snapCoord(tExit.x, GRID);
  const gy = snapCoord(tExit.y, GRID);

  // Portals: allow standing on start/goal even if float near keep-out edge
  const portals = [
    { x: sx, y: sy },
    { x: gx, y: gy },
  ];
  const isPortal = (x: number, y: number) =>
    portals.some((p) => Math.abs(p.x - x) <= GRID / 2 && Math.abs(p.y - y) <= GRID / 2);

  const blocked = (x: number, y: number) => !isPortal(x, y) && pointBlocked(x, y, keepOut);

  const minX = Math.min(sx, gx) - SEARCH_PAD;
  const maxX = Math.max(sx, gx) + SEARCH_PAD;
  const minY = Math.min(sy, gy) - SEARCH_PAD;
  const maxY = Math.max(sy, gy) + SEARCH_PAD;

  const open = new MinHeap();
  const bestG = new Map<string, number>();
  const nodes: ANode[] = [];
  const k = (x: number, y: number, d: Dir) => `${x},${y},${d}`;

  const start: ANode = {
    x: sx,
    y: sy,
    dir: startDir,
    g: 0,
    f: manhattan({ x: sx, y: sy }, { x: gx, y: gy }),
    idx: 0,
    parent: -1,
  };
  nodes.push(start);
  open.push(start);
  bestG.set(k(sx, sy, startDir), 0);

  let goal: ANode | null = null;
  let iters = 0;

  while (open.size && iters < MAX_ITERS) {
    iters++;
    const cur = open.pop()!;
    if ((bestG.get(k(cur.x, cur.y, cur.dir)) ?? Infinity) < cur.g - 1e-6) continue;

    if (Math.abs(cur.x - gx) <= GRID / 2 && Math.abs(cur.y - gy) <= GRID / 2) {
      goal = cur;
      break;
    }

    for (const { dx, dy, dir } of STEP) {
      if (dir === opposite(cur.dir)) continue;
      const nx = cur.x + dx;
      const ny = cur.y + dy;
      if (nx < minX || nx > maxX || ny < minY || ny > maxY) continue;
      if (blocked(nx, ny)) continue;
      if (!isPortal(nx, ny) && !stepClear(cur.x, cur.y, nx, ny, keepOut)) continue;

      const g = cur.g + GRID + (dir === cur.dir ? 0 : BEND_PENALTY);
      const key = k(nx, ny, dir);
      if (g >= (bestG.get(key) ?? Infinity)) continue;
      bestG.set(key, g);

      const next: ANode = {
        x: nx,
        y: ny,
        dir,
        g,
        f: g + manhattan({ x: nx, y: ny }, { x: gx, y: gy }),
        idx: nodes.length,
        parent: cur.idx,
      };
      nodes.push(next);
      open.push(next);
    }
  }

  if (!goal) {
    for (const n of nodes) {
      if (Math.abs(n.x - gx) <= GRID && Math.abs(n.y - gy) <= GRID) {
        if (!goal || n.g < goal.g) goal = n;
      }
    }
  }
  if (!goal) return null;

  const rev: Pt[] = [{ x: tExit.x, y: tExit.y }];
  let cur: ANode | null = goal;
  const seen = new Set<string>();
  let hops = 0;
  while (cur && hops++ < 2500) {
    const id = `${cur.x},${cur.y}`;
    if (!seen.has(id)) {
      rev.push({ x: cur.x, y: cur.y });
      seen.add(id);
    }
    if (cur.parent < 0) break;
    cur = nodes[cur.parent] ?? null;
  }
  rev.reverse();
  if (!almostEq(rev[0]!.x, sExit.x) || !almostEq(rev[0]!.y, sExit.y)) {
    rev.unshift({ ...sExit });
  }
  return simplifyCollinear(rev);
}

/** Detour above/below/beside the union of keep-outs (always orthogonal, no reverse-first). */
function aroundUnion(sExit: Pt, tExit: Pt, startDir: Dir, keepOut: ObstacleRect[]): Pt[][] {
  if (!keepOut.length) return [];
  let x1 = Infinity;
  let y1 = Infinity;
  let x2 = -Infinity;
  let y2 = -Infinity;
  for (const o of keepOut) {
    x1 = Math.min(x1, o.x);
    y1 = Math.min(y1, o.y);
    x2 = Math.max(x2, o.x + o.width);
    y2 = Math.max(y2, o.y + o.height);
  }
  const pad = GRID;
  const top = y1 - pad;
  const bottom = y2 + pad;
  const left = x1 - pad;
  const right = x2 + pad;
  const v = dirVec(startDir);
  const fwd = { x: sExit.x + v.x * GRID * 2, y: sExit.y + v.y * GRID * 2 };

  const raw: Pt[][] = [
    [{ x: sExit.x, y: top }, { x: tExit.x, y: top }],
    [{ x: sExit.x, y: bottom }, { x: tExit.x, y: bottom }],
    [{ x: left, y: sExit.y }, { x: left, y: tExit.y }],
    [{ x: right, y: sExit.y }, { x: right, y: tExit.y }],
  ];

  const out: Pt[][] = [];
  for (const mids of raw) {
    const first = mids[0]!;
    const dx = Math.sign(first.x - sExit.x);
    const dy = Math.sign(first.y - sExit.y);
    const reverse =
      (v.x !== 0 && dx === -v.x) || (v.y !== 0 && dy === -v.y);
    if (reverse) out.push([fwd, ...mids]);
    else out.push(mids);
  }
  return out;
}

function buildFull(start: Pt, sExit: Pt, mid: Pt[], tExit: Pt, end: Pt): Pt[] {
  const inner = mid.filter(
    (p, i) =>
      !(i === 0 && almostEq(p.x, sExit.x) && almostEq(p.y, sExit.y)) &&
      !(i === mid.length - 1 && almostEq(p.x, tExit.x) && almostEq(p.y, tExit.y)),
  );
  return finalize([start, sExit, ...inner, tExit, end]);
}

export function buildFastOrthoPath(args: {
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  sourcePosition: Position;
  targetPosition: Position;
  laneOffset?: number;
  routeOffset?: number;
  labelNudgeY?: number;
  labelT?: number;
}): { path: string; labelX: number; labelY: number; points: Pt[] } {
  const sDir = posToDir(args.sourcePosition);
  const start = { x: args.sourceX, y: args.sourceY };
  const end = { x: args.targetX, y: args.targetY };
  const sExit = stubTip(start, sDir);
  const tExit = stubTip(end, posToDir(args.targetPosition));
  const vh = { x: sExit.x, y: tExit.y };
  const hv = { x: tExit.x, y: sExit.y };
  const v = dirVec(sDir);
  const reverseH = v.x !== 0 && Math.sign(hv.x - sExit.x) === -v.x;
  const reverseV = v.y !== 0 && Math.sign(vh.y - sExit.y) === -v.y;
  let mid: Pt[];
  if (!reverseV) mid = [vh];
  else if (!reverseH) mid = [hv];
  else {
    const fwd = { x: sExit.x + v.x * GRID * 2, y: sExit.y + v.y * GRID * 2 };
    mid = [fwd, { x: tExit.x, y: fwd.y }];
  }
  let points = buildFull(start, sExit, mid, tExit, end);
  const off = snapCoord((args.laneOffset ?? 0) + (args.routeOffset ?? 0), EDGE_DASH_PERIOD);
  if (off) points = finalize(applyLaneOffset(points, off));
  const label = labelPos(points, [], args.labelT != null ? 0 : args.labelNudgeY ?? 0, args.labelT);
  return { path: toSvgPath(points), labelX: label.x, labelY: label.y, points };
}

export function buildAvoidPath(args: {
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  sourcePosition: Position;
  targetPosition: Position;
  obstacles: ObstacleRect[];
  laneOffset?: number;
  routeOffset?: number;
  labelNudgeY?: number;
  labelT?: number;
  endpointIds?: string[];
  fast?: boolean;
}): { path: string; labelX: number; labelY: number; points: Pt[] } {
  if (args.fast) return buildFastOrthoPath(args);

  const sDir = posToDir(args.sourcePosition);
  const tDir = posToDir(args.targetPosition);
  const start = { x: args.sourceX, y: args.sourceY };
  const end = { x: args.targetX, y: args.targetY };
  const sExit = stubTip(start, sDir);
  const tExit = stubTip(end, tDir);

  // ALL devices including endpoints — prevents tunneling through blocks
  const keepOut = args.obstacles.map((o) => inflate(o, BLOCK_BARRIER));

  const candidates: Pt[][] = [];

  const astarPath = astarOrtho(sExit, tExit, sDir, keepOut);
  if (astarPath) candidates.push(buildFull(start, sExit, astarPath, tExit, end));

  for (const mids of aroundUnion(sExit, tExit, sDir, keepOut)) {
    candidates.push(buildFull(start, sExit, mids, tExit, end));
  }

  // Short ortho if clear
  for (const mids of [[{ x: sExit.x, y: tExit.y }], [{ x: tExit.x, y: sExit.y }]]) {
    candidates.push(buildFull(start, sExit, mids, tExit, end));
  }

  let best: Pt[] | null = null;
  let bestScore = Infinity;
  for (const pts of candidates) {
    if (pts.length < 4) continue;
    if (corridorHits(pts, keepOut)) continue;
    const bends = Math.max(0, pts.length - 2);
    const score = pathLength(pts) + bends * BEND_PENALTY * 0.25;
    if (score < bestScore) {
      bestScore = score;
      best = pts;
    }
  }

  if (!best) {
    // Last resort: far above everything
    let yTop = Math.min(sExit.y, tExit.y) - SEARCH_PAD / 2;
    for (const o of keepOut) yTop = Math.min(yTop, o.y - GRID);
    best = buildFull(
      start,
      sExit,
      [
        { x: sExit.x, y: yTop },
        { x: tExit.x, y: yTop },
      ],
      tExit,
      end,
    );
  }

  const off = snapCoord((args.laneOffset ?? 0) + (args.routeOffset ?? 0), EDGE_DASH_PERIOD);
  if (off) {
    const shifted = finalize(applyLaneOffset(best, off));
    if (!corridorHits(shifted, keepOut)) best = shifted;
  }

  const nudge = args.labelT != null ? 0 : args.labelNudgeY ?? 0;
  const label = labelPos(best, keepOut, nudge, args.labelT);
  return { path: toSvgPath(best), labelX: label.x, labelY: label.y, points: best };
}

export function computeLaneOffset(
  edgeId: string,
  source: string,
  target: string,
  peers: Array<{ id: string; source: string; target: string }>,
): number {
  const undirected = (a: string, b: string) => (a < b ? `${a}|${b}` : `${b}|${a}`);
  const myKey = undirected(source, target);
  const group = peers
    .filter((e) => undirected(e.source, e.target) === myKey)
    .map((e) => e.id)
    .sort();
  const index = group.indexOf(edgeId);
  if (index < 0 || group.length <= 1) return 0;
  return (index - (group.length - 1) / 2) * LANE_GAP;
}

export function computeLabelNudgeY(
  edgeId: string,
  source: string,
  target: string,
  peers: Array<{ id: string; source: string; target: string }>,
): number {
  const related = peers
    .filter(
      (e) =>
        e.source === source ||
        e.target === source ||
        e.source === target ||
        e.target === target,
    )
    .map((e) => e.id)
    .sort();
  const unique = [...new Set(related)];
  const index = unique.indexOf(edgeId);
  if (index < 0 || unique.length <= 1) return 0;
  return (index - (unique.length - 1) / 2) * LANE_GAP;
}
